import React, { useEffect, useState, useRef } from 'react';
import { t } from 'i18next';
import cx from 'classnames';
import { PartnerScoreDataAvailabilityArrowSort } from '@/components/base/Icons/PartnerScoreDataAvailabilityArrowSort';
import { TableHeaderProps } from './utils/TableHeaderTypes';
import { isAllQuarters } from '@/api/hcp/partnerscore/utils';
import styles from './TableHeader.module.css';

const TableHeader: React.FC<TableHeaderProps> = ({
  handleSort,
  sortConfig,
  allYearQuarters,
  selectedYear,
  yearRef,
  selectedQuarter,
  hasDataForYear,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const initialPositionRef = useRef<number | null>(null);

  useEffect(() => {
    const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;

      const { overflowY, overflowX } = window.getComputedStyle(element);
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') {
        return element;
      }

      return findScrollContainer(element.parentElement);
    };

    const handleScroll = () => {
      if (headerRef.current) {
        const scrollContainer = findScrollContainer(headerRef.current);

        if (scrollContainer) {
          if (initialPositionRef.current === null) {
            initialPositionRef.current = headerRef.current.offsetTop;
          }

          const isScrolledDown = scrollContainer.scrollTop > 0;

          if (isScrolledDown !== isScrolled) {
            setIsScrolled(isScrolledDown);
          }
        }
      }
    };

    const scrollContainer = headerRef.current ? findScrollContainer(headerRef.current) : null;

    if (scrollContainer) {
      handleScroll();
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isScrolled]);

  return (
    <thead ref={headerRef} className={isScrolled ? `${styles.stickyHeader} ${styles.stickyHeaderScrolled}` : ''}>
      <tr>
        <th className={styles.tableHeaderCountries} rowSpan={2}>
          <div className={styles.flexContainer} onClick={handleSort}>
            {t('main.partnerScore.partnerScoreDataAvailability.columnName')}
            <div className={`${styles.arrowSortIcon} ${styles.cursorPointer}`}>
              <div className={sortConfig.direction === 'asc' ? styles.arrowUp : styles.arrowDown}>
                <PartnerScoreDataAvailabilityArrowSort />
              </div>
            </div>
          </div>
        </th>
        {allYearQuarters.map(({ year }) => (
          <th
            ref={year === selectedYear ? yearRef : null}
            key={`year-${year}`}
            className={cx(styles.tableHeader, {
              [styles.selectedYear]: year === selectedYear,
            })}
            colSpan={4}
            data-has-year-data={hasDataForYear(year)}
          >
            {year}
          </th>
        ))}
      </tr>
      <tr>
        {allYearQuarters.flatMap(({ year, quarters }) =>
          quarters.map((quarter: number) => (
            <th
              key={`year-${year}-quarter-${quarter}`}
              className={cx(styles.tableHeader, {
                [styles.selectedQuarter]: year === selectedYear && quarter === selectedQuarter,
                [styles.selectedQuarterAllLeft]:
                  year === selectedYear && isAllQuarters(selectedQuarter) && quarter === 1,
                [styles.selectedQuarterAllMiddle]:
                  year === selectedYear && isAllQuarters(selectedQuarter) && (quarter === 2 || quarter === 3),
                [styles.selectedQuarterAllRight]:
                  year === selectedYear && isAllQuarters(selectedQuarter) && quarter === 4,
              })}
            >
              {`Q${quarter}`}
            </th>
          )),
        )}
      </tr>
    </thead>
  );
};

export default TableHeader;
